import _ from 'lodash';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from './modules';
import {
  ownProfileStatusStateSelector,
  ownProfilesSelector,
  profileForApplicationStatusSelector,
  profilesForApplicationSelector
} from './selectors';
import { TRequirementCheck } from '../../../../../../shared/types/recruitmentTypes';
import { featuresSelector } from '../../../../../../shared/frontend/scripts/selectors/global';
import { TRecruitmentProfile } from '../../libs/store';

const { requestProfilesForApplication, loadOwnProfiles } = actions;

type TUseRecruitmentProfileConfig = {
  transform?: (profiles: TRecruitmentProfile[]) => any;
};
/*
 * Fetches all of a user's OWN profiles if they haven't yet been requested.
 * Returns the serialized/ui version of the profile list and status states.
 */
export const useRecruitmentProfiles = (config?: TUseRecruitmentProfileConfig) => {
  const { transform = (p: any) => p } = config || {};
  const dispatch = useDispatch();
  const { loading, loaded, error } = useSelector(ownProfileStatusStateSelector);
  const ownProfiles = useSelector(ownProfilesSelector);
  const { recruitment_create, authenticated } = useSelector(featuresSelector);

  useEffect(() => {
    if (!loading && !loaded && _.isEmpty(ownProfiles) && recruitment_create && authenticated) {
      dispatch(loadOwnProfiles());
    }
  }, [loading, loaded, ownProfiles, recruitment_create, authenticated, dispatch]);

  return {
    ownProfiles: transform(ownProfiles),
    loading,
    loaded,
    error
  };
};

type TUseValidRecruitmentProfilesConfig = {
  profile: TRecruitmentProfile;
};
/*
 * Fetches the user's matching recruitment profiles for the profile passed in the config.
 */
export type TChecks = {
  valid: boolean;
  checks: TRequirementCheck[];
};
export type TRequirementCheckedRecruitmentProfile = {
  targetProfile: TRecruitmentProfile;
  sourceProfile: TRecruitmentProfile;
  checks: TChecks;
};
type RecruitmentProfilesForApplication = {
  profiles: TRequirementCheckedRecruitmentProfile[];
  loading: boolean;
  loaded: boolean;
  error: boolean;
};
export const useRecruitmentProfileForApplication = (
  config: TUseValidRecruitmentProfilesConfig
): RecruitmentProfilesForApplication => {
  const { profile } = config || {};

  const dispatch = useDispatch();
  const { loading, loaded, error } = useSelector(profileForApplicationStatusSelector(profile.id));

  const profiles: TRequirementCheckedRecruitmentProfile[] = useSelector(profilesForApplicationSelector(profile.id));

  const { message_center } = useSelector(featuresSelector);

  // Use useEffect to dispatch after sagas are injected
  useEffect(() => {
    if (!loading && !loaded && message_center) {
      dispatch(requestProfilesForApplication(profile.id));
    }
  }, [loading, loaded, message_center, profile.id, dispatch]);

  return { profiles, loading, loaded, error };
};
